import { useState, useRef, type ReactNode } from "react";
import {
  Alert,
  type AlertColor,
  Box,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

export interface CompactAlertProps {
  /** Sévérité (info / success / warning / error). */
  severity: AlertColor;
  /** Libellé court affiché sur une ligne. */
  message: ReactNode;
  /** Détail riche affiché dans le popover déclenché par l'icône info. */
  detail?: ReactNode;
  /** Icône principale de l'alerte (override de l'icône par défaut MUI). */
  icon?: ReactNode;
  /** Styles MUI complémentaires. */
  sx?: object;
}

/**
 * Alerte compacte sur une ligne avec icône info pour afficher le détail dans un popover.
 * Utiliser à la place d'`Alert` + `AlertTitle` quand le détail est long et alourdit l'UI.
 */
export function CompactAlert({
  severity,
  message,
  detail,
  icon,
  sx,
}: CompactAlertProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const hasDetail = detail !== undefined && detail !== null && detail !== false;

  return (
    <>
      <Alert
        severity={severity}
        icon={icon}
        sx={{
          alignItems: "center",
          py: 0.5,
          "& .MuiAlert-message": {
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
            py: 0.5,
          },
          ...sx,
        }}
      >
        <Typography
          variant="body2"
          sx={{ flex: 1, fontWeight: 600, lineHeight: 1.3 }}
        >
          {message}
        </Typography>
        {hasDetail && (
          <IconButton
            ref={buttonRef}
            size="small"
            aria-label="Voir le détail"
            onClick={() => setAnchorEl(buttonRef.current)}
            sx={{ color: "inherit", opacity: 0.8 }}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        )}
      </Alert>
      {hasDetail && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: { maxWidth: 360, p: 2 },
            },
          }}
        >
          <Box sx={{ typography: "body2", color: "text.primary" }}>{detail}</Box>
        </Popover>
      )}
    </>
  );
}
